import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useHistory } from "react-router-dom";
import qs from "qs";
import {
  StyledFADLogo,
  DrugsAZDescriptionWithImage,
  FADLogoContainer,
} from "./_.styled";
import { Sticky } from "../../components/Sticky/_.styled";
import {
  Page,
  PageDescription,
  LinksList,
  NarrowFilter,
  PageTitle,
} from "../../components";
import { useDispatch, useStore } from "../../store";
import MainSpinner from "../../components/MainSpinner";
import SeeAlso from "../../components/SeeAlso";
import { isScrolledIntoView } from "../../util/viewport-utils";
import { logDrugClicked, logDrugFilterClicked } from "../../util/analytics";
import { useSearch } from "../../hooks/useSearch";
import { useBaseSearchTransform } from "../../hooks/useBaseSearchTransform";
import {
  TabContext,
  AlphaTabs,
  AlphaTab,
} from "discover.medical.react-component-library";
import { getViewModel } from "../../store/browse/actions";
import NotFound from "../../components/NotFound";
import ServerError from "../../components/ServerError";
import ExcludedContentMessage from "../../components/ExcludedContentMessage";

const DrugsAZ = () => {
  const { slug } = useParams();
  const browsePath = slug;
  const titleRef = useRef(null);

  const isLoading = useStore().browse?.[browsePath]?.isLoading;
  const hasError = useStore().browse?.[browsePath]?.hasError;
  const statusCode = useStore().browse?.[browsePath]?.statusCode;
  const viewModel = useStore().browse?.[browsePath]?.viewModel;
  const dispatch = useDispatch();

  const { title, description, topics, alphaTabs, logo, seeAlso } =
    viewModel || {};
  const excludedContentMessage = viewModel?.excludedContent?.message;

  const history = useHistory();
  const {
    location,
    location: { hash },
  } = history;
  const startsWith = hash.substring(1) || "A";
  const activeTab = alphaTabs?.indexOf(startsWith);

  const [isNavigationSticky, setNavigationSticky] = useState(false);
  const handleScroll = () => {
    setNavigationSticky(!isScrolledIntoView(titleRef.current));
  };
  const queryParams = qs.parse(location.search, {
    ignoreQueryPrefix: true,
  });

  const {
    searchTerm: searchKey,
    setSearchTerm: setSearchKey,
    handleSearchTermChange: handleSearchKeyChange,
    searchUtils,
  } = useSearch(queryParams.searchKey || "");

  const transformedTopics = useBaseSearchTransform({
    items: topics,
    searchUtils,
  });

  useEffect(() => {
    getViewModel(dispatch, browsePath, { startsWith });
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [dispatch, browsePath, startsWith]);

  useEffect(() => {
    /* If the user arrive at drug guide by clicking back then the searchKey
    and the associated query params are erased */
    if (history.action === "POP") {
      setSearchKey(queryParams.searchKey || "");
    }
  }, [history.action, queryParams.searchKey, setSearchKey]);

  useEffect(() => {
    const currentQsParams = qs.parse(history.location.search, {
      ignoreQueryPrefix: true,
    });
    const qsValue = qs.stringify({
      ...currentQsParams,
      searchKey: searchKey || undefined,
    });
    const { pathname, hash: currentHash } = history.location;
    history.replace(
      `${pathname}?${qsValue}${currentHash}`,
      history.location.state
    );
  }, [searchKey, history]);

  function handleDrugClick({ label, an }) {
    logDrugClicked({
      title: label,
      recordAn: an,
      filterInput: searchKey,
    });
  }

  const handleTabChange = (e, index) => {
    const character = alphaTabs[index];
    setSearchKey("");
    getViewModel(dispatch, browsePath, { startsWith: character });
    const path = `${history.location.pathname}#${character}`;
    history.push(path);
    logDrugFilterClicked({ character });
  };

  if (hasError) return statusCode === 404 ? <NotFound /> : <ServerError />;

  return (
    <Page data-testid="drug-guide-root">
      <DrugsAZDescriptionWithImage
        data-testid="drug-guide-header"
        ref={titleRef}
      >
        <div>
          <PageTitle>{title}</PageTitle>
          <PageDescription data-testid="drug-guide-description" element="div">
            {description}
          </PageDescription>
        </div>
        {logo !== undefined && (
          <FADLogoContainer data-testid="drug-guide-media">
            <StyledFADLogo src={logo?.src} alt={logo?.alt} />
          </FADLogoContainer>
        )}
      </DrugsAZDescriptionWithImage>
      {seeAlso && <SeeAlso links={seeAlso} />}
      {alphaTabs?.length > 0 && (
        <TabContext activeTab={activeTab} onChange={handleTabChange}>
          <Sticky sticky={isNavigationSticky}>
            <AlphaTabs
              scrollButtons={true}
              data-testid="char-list"
              aria-label="drugs by letter"
            >
              {alphaTabs?.map((tab) => (
                <AlphaTab key={tab} linkComponent={"a"} href={`#${tab}`}>
                  {tab}
                </AlphaTab>
              ))}
            </AlphaTabs>
            {!excludedContentMessage && (
              <NarrowFilter
                data-testid="drug-guide-search-input"
                onChange={handleSearchKeyChange}
                value={searchKey}
                placeholder={`Find in '${startsWith}' list`}
              />
            )}
          </Sticky>
          {isLoading ? (
            <MainSpinner />
          ) : excludedContentMessage ? (
            <ExcludedContentMessage message={excludedContentMessage} />
          ) : (
            <LinksList
              items={transformedTopics}
              onItemClick={handleDrugClick}
              noDataText="No drug matches for the term provided"
            />
          )}
        </TabContext>
      )}
    </Page>
  );
};

export default DrugsAZ;
